/* MMA Fight Store: Shop by Discipline v2 (sections/mfs-shop-by-discipline.liquid)
   Gear finder: sport -> level -> results grid with category chips, "My level only", sort, and a kit deal.
   Vanilla JS, no dependencies. Data: sections/mfs-sbd-data.liquid through the Section Rendering API. */
(function () {
  "use strict";

  const LEVELS = [
    { key: "beginner", label: "Beginner", angle: -90 },
    { key: "intermediate", label: "Intermediate", angle: -30 },
    { key: "amateur", label: "Amateur", angle: 30 },
    { key: "professional", label: "Professional", angle: 90 },
  ];
  const COLOURS = new Set("black white red blue green gold silver pink purple yellow orange brown grey gray navy maroon camo tan olive sand cream khaki ash mist nebula bone charcoal teal cyan".split(" "));
  // Words that describe the product type, not its design line (used when matching sets)
  const TYPE_WORDS = new Set(("glove gloves shin guard guards mouthguard mouthguards mouth headgear head gear wrap wraps hand hands singlet singlets short shorts rashguard rashguards rash " +
    "spat spats belt belts boot boots shoe shoes groin protector cup pad pads mitt mitts focus bag bags boxing muay thai mma bjj jiu jitsu gi kick kickboxing training sparring " +
    "series size the and for with mens men women womens unisex adult male female new pro premium classic leather limited edition elite deluxe original essential " +
    "competition approved long short sleeve top tops tee shirt t-shirt trunks jersey").split(" "));
  const KIDS = /\b(youth|junior|kids?)\b/i;
  const WOMENS = /\b(wom[ae]n'?s?|ladies|female|girls?)\b/i;
  // Pairs by colourway alone only make sense inside these families
  const SET_GROUPS = [/glove|shin|wrap/, /short|rash|top|shirt|singlet|jersey|trunk|spats|tee/];
  const MAX_PAGES = 8;
  // Product data cache: bump CACHE_PREFIX when the cached shape or tagging changes; entries expire after 10 min
  const CACHE_PREFIX = "mfs-sbd3";
  const CACHE_MS = 10 * 60 * 1000;
  const MOBILE = window.matchMedia("(max-width: 639px)");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const money = (n) => "$" + n.toFixed(2);
  const list = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);
  const normType = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "").replace(/s$/, "");
  // Product types that are one category to shoppers (MMA and BJJ shorts are the same shorts, targeted differently)
  const TYPE_MERGE = { mmashort: "MMA & BJJ Shorts", bjjshort: "MMA & BJJ Shorts" };
  const MERGE_KEY = { bjjshort: "mmashort" };
  const typeOf = (ty) => { const n = normType(ty) || "other"; return { key: MERGE_KEY[n] || n, label: TYPE_MERGE[n] || ty || "Other" }; };
  const fill = (tpl, vars) => String(tpl || "").replace(/\{(\w+)\}/g, (_, k) => (k in vars ? vars[k] : ""));

  // Size values: "Medium" -> "m", "X Large" -> "xl", "US 8.0" -> "us8", "16 oz" -> "16oz", "S/M" -> ["s", "m"]
  function normSize(s) {
    let x = String(s || "").toLowerCase().replace(/ounces?/g, "oz").replace(/\s+/g, "").replace(/(\d)\.0(?!\d)/g, "$1");
    x = x.replace(/^(x*)large$/, "$1l").replace(/^(x*)small$/, "$1s").replace(/^medium$/, "m");
    x = x.replace(/^extra/, "x");
    x = x.replace(/^(a\d)[lh]$/, "$1"); // BJJ gi cuts: A2L / A2H are A2
    return x;
  }
  const sizeParts = (s) => String(s || "").split("/").map(normSize).filter(Boolean);

  function init(root) {
    const cfgNode = root.querySelector("[data-sbd-config]");
    if (!cfgNode) return;
    const cfg = JSON.parse(cfgNode.textContent);
    if (typeof window.MFS_SBD_PROTOTYPE === "function") Object.assign(cfg, window.MFS_SBD_PROTOTYPE(cfg));

    // ---- null-safe DOM helpers (a missing element must never stop rendering) ----
    const q = (sel) => root.querySelector(sel);
    const setText = (sel, text) => { const el = q(sel); if (el) el.textContent = text; return el; };
    const setHTML = (sel, html) => { const el = q(sel); if (el) el.innerHTML = html; return el; };
    const show = (sel, on) => { const el = q(sel); if (el) el.hidden = !on; return el; };

    const disciplines = cfg.disciplines.filter((d) => d.collection);
    const discByKey = Object.fromEntries(disciplines.map((d) => [d.key, d]));
    const core = new Set(list(cfg.coreSizes).map(normSize));
    const thinVendors = new Set(list(cfg.thinVendors).map((v) => v.toLowerCase()));
    const hideVendors = new Set(list(cfg.hideVendors).map((v) => v.toLowerCase()));
    const hideWords = list(cfg.hideKeywords).map((w) => new RegExp("\\b" + w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\b", "i"));
    const compRules = list(cfg.compLines).map((w) => w.toLowerCase().split(/\s+/).filter(Boolean));

    const byHandle = new Map();
    const loads = {}; // discipline key -> Promise
    const status = {}; // discipline key -> loading | ready | error
    const loadedAt = {}; // discipline key -> when its product data was fetched (ms)

    const state = {
      step: "discipline",
      selected: [],
      level: null,
      levelOnly: true,
      sort: "recommended",
      category: "all",
      shown: 0,
      armed: new Map(), // handle -> { ds, variantId, label, price, title }
      picking: null,
      moreOpen: false,
      signup: "open", // open | signed | dismissed
      error: "",
      kitError: "",
    };

    // ---------------- Data ----------------
    function derive(p) {
      const variants = (p.v || []).map((v) => ({ id: v.id, title: v.t, price: v.p / 100, available: !!v.a, parts: String(v.t).split(" / ") }));
      const options = p.o || [];
      let sizeIdx = options.findIndex((o) => /size|weight|oz/i.test(o));
      if (sizeIdx === -1 && options.length === 1 && variants.length > 1) sizeIdx = 0;
      const sizeOf = (v) => (sizeIdx > -1 ? v.parts[sizeIdx] : "");
      const values = [...new Set(variants.map(sizeOf))];
      const oneSize = variants.length === 1 || sizeIdx === -1 || values.every((x) => /^(one ?size|default title|os)$/i.test(x));
      const availSizes = [...new Set(variants.filter((v) => v.available).map(sizeOf))];
      const coreAvail = oneSize ? 1 : availSizes.filter((s) => sizeParts(s).some((x) => core.has(x))).length;
      const avail = variants.filter((v) => v.available);
      const price = avail.length ? Math.min(...avail.map((v) => v.price)) : 0;
      const minPrice = variants.length ? Math.min(...variants.map((v) => v.price)) : 0;
      const title = p.t || "";
      const vendor = (p.vn || "").toLowerCase();
      const cat = typeOf(p.ty);
      return {
        handle: p.h, title, type: cat.label, typeKey: cat.key, vendor, url: p.u, image: p.img,
        levels: p.lv || [], bestSeller: !!p.bs, pairs: p.pr || [], options, variants, sizeIdx, oneSize,
        sizeCount: values.length, availCount: availSizes.length, coreAvail, price, minPrice,
        firstAvail: avail[0] || null, kids: KIDS.test(title), womens: WOMENS.test(title),
        comp: compRules.some((words) => { const hay = (vendor + " " + title.toLowerCase()).split(/[^a-z0-9]+/); return words.every((w) => hay.includes(w)); }),
        ds: [], rank: Infinity, toks: null, colours: null,
      };
    }

    // Decided 30 Sep 2026: sold out never; untagged never; one-size shows if available;
    // sized needs a core size in stock and, unless thin stock is allowed, two sizes in stock.
    function visible(p) {
      if (!p.firstAvail || !p.levels.length) return false;
      if (hideVendors.has(p.vendor)) return false;
      if (hideWords.some((re) => re.test(p.title))) return false;
      if (p.minPrice < cfg.minPrice) return false;
      if (p.oneSize) return true;
      if (p.coreAvail < 1) return false;
      return p.availCount >= 2 || thinVendors.has(p.vendor) || p.sizeCount <= 3;
    }

    function ingest(raw, discKey, into = byHandle) {
      raw.forEach((r, i) => {
        let p = into.get(r.h);
        if (!p) { p = derive(r); into.set(p.handle, p); }
        if (!p.ds.includes(discKey)) p.ds.push(discKey);
        p.rank = Math.min(p.rank, i); // collection feed order = best-seller rank
      });
    }

    async function fetchPage(d, page) {
      if (cfg.source) return cfg.source(d.key, page);
      const url = `${cfg.root}collections/${encodeURIComponent(d.collection)}?section_id=${cfg.dataSection}&page=${page}`;
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), "text/html");
      const node = doc.querySelector("[data-sbd-page]");
      if (!node) throw new Error("No data");
      return JSON.parse(node.textContent);
    }

    async function fetchAll(d) {
      const first = await fetchPage(d, 1);
      const pages = Math.min(first.pages || 1, MAX_PAGES);
      const rest = pages > 1 ? await Promise.all(Array.from({ length: pages - 1 }, (_, i) => fetchPage(d, i + 2))) : [];
      return [first, ...rest].flatMap((x) => x.products || []);
    }

    const cacheKey = (k) => `${CACHE_PREFIX}:${cfg.cacheVersion}:${k}`;
    // Drop entries from older prefixes, older versions and expired ones so stale tags and stock never linger
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (!k || !k.startsWith("mfs-sbd")) continue;
        let fresh = k.startsWith(`${CACHE_PREFIX}:${cfg.cacheVersion}:`);
        if (fresh) { try { fresh = Date.now() - JSON.parse(sessionStorage.getItem(k)).at < CACHE_MS; } catch (e) { fresh = false; } }
        if (!fresh) sessionStorage.removeItem(k);
      }
    } catch (e) { /* storage blocked */ }

    function load(key) {
      if (loads[key] && status[key] !== "error") return loads[key];
      status[key] = "loading";
      loads[key] = (async () => {
        const d = discByKey[key];
        try {
          const c = JSON.parse(sessionStorage.getItem(cacheKey(key)) || "null");
          if (c && Date.now() - c.at < CACHE_MS) { ingest(c.list, key); loadedAt[key] = c.at; status[key] = "ready"; return; }
        } catch (e) { /* storage blocked */ }
        try {
          const raw = await fetchAll(d);
          ingest(raw, key);
          status[key] = "ready";
          stored(key, raw, Date.now());
        } catch (err) {
          status[key] = "error";
        }
      })();
      // Whatever step the shopper is on when this lands, results re-render from current state (no race).
      loads[key].then(() => { if (state.step === "results") renderResults(false); });
      return loads[key];
    }
    function stored(key, raw, at) {
      loadedAt[key] = at;
      try { sessionStorage.setItem(cacheKey(key), JSON.stringify({ at, list: raw })); } catch (e) { /* full */ }
    }

    // A page left open keeps its products in memory, so the 10 min expiry alone never reaches it. When the shopper
    // comes back to the tab (or clicks in the finder) after that, refetch every loaded sport in the background and
    // swap the new data in one go, so the grid never empties and tag or stock changes show without a reload.
    let refreshing = false;
    async function refreshIfStale() {
      const keys = Object.keys(loadedAt).filter((k) => status[k] === "ready");
      if (refreshing || document.visibilityState !== "visible" || !keys.some((k) => Date.now() - loadedAt[k] >= CACHE_MS)) return;
      refreshing = true;
      try {
        const fresh = await Promise.all(keys.map((k) => fetchAll(discByKey[k]).then((raw) => [k, raw])));
        const next = new Map();
        fresh.forEach(([k, raw]) => ingest(raw, k, next));
        // a sport that finished loading while this ran keeps its products
        byHandle.forEach((p, h) => {
          const other = p.ds.filter((d) => !keys.includes(d));
          if (!other.length) return;
          const n = next.get(h);
          if (!n) { p.ds = other; next.set(h, p); return; }
          other.forEach((d) => { if (!n.ds.includes(d)) n.ds.push(d); });
          n.rank = Math.min(n.rank, p.rank);
        });
        byHandle.clear();
        next.forEach((p, h) => byHandle.set(h, p));
        const at = Date.now();
        fresh.forEach(([k, raw]) => stored(k, raw, at));
        if (state.step === "results" && !state.picking) renderResults(false);
      } catch (err) { /* keep what's showing; the next visit or click tries again */ }
      refreshing = false;
    }

    // ---------------- Ordering ----------------
    const dearFirst = () => state.level === "amateur" || state.level === "professional";
    const levelFilterOn = () => {
      if (!state.levelOnly || !state.level) return false;
      // a mixed selection filters when any selected sport filters at this level
      return state.selected.some((k) => {
        const m = (discByKey[k] || {}).levelMode || "full";
        return m === "full" || (m === "beginner_only" && state.level === "beginner");
      });
    };
    const matchesLevel = (p) => p.levels.includes(state.level);
    const isCompKit = (p) => p.comp && state.level === "amateur" && p.ds.includes("boxing") && state.selected.includes("boxing");

    function priorityList() {
      const out = [];
      state.selected.forEach((k) => (discByKey[k].priority || []).forEach((t) => { const n = t && typeOf(t).key; if (n && !out.includes(n)) out.push(n); }));
      return out;
    }

    // Everything that could show for this sport + level, before the category chip
    function pool() {
      const set = new Set();
      state.selected.forEach((k) => { if (status[k] === "ready") byHandle.forEach((p) => { if (p.ds.includes(k)) set.add(p); }); });
      let items = [...set].filter(visible);
      if (levelFilterOn()) items = items.filter((p) => matchesLevel(p) || isCompKit(p));
      return items;
    }

    function withinCategory(a, b) {
      if (!state.levelOnly || !levelFilterOn()) {
        const m = (matchesLevel(b) ? 1 : 0) - (matchesLevel(a) ? 1 : 0);
        if (m) return m;
      }
      const cov = (p) => (p.oneSize || p.coreAvail >= 2 ? 0 : 1); // well covered before thin
      const c = cov(a) - cov(b);
      if (c) return c;
      // Beginner/Intermediate: best value first, strictly by price. Amateur/Professional: best sellers first,
      // dearer first between equals (a pure price sort would bury popular pro gear like SKS Tiger shorts).
      if (dearFirst()) {
        if (a.rank !== b.rank) return a.rank - b.rank;
        if (a.price !== b.price) return b.price - a.price;
      } else {
        if (a.price !== b.price) return a.price - b.price;
        if (a.rank !== b.rank) return a.rank - b.rank;
      }
      return b.coreAvail - a.coreAvail || b.availCount - a.availCount;
    }

    // Rotate through categories in priority order so each page reads like a kit.
    function rotate(items) {
      const prio = priorityList();
      const buckets = new Map();
      items.forEach((p) => {
        if (!buckets.has(p.typeKey)) buckets.set(p.typeKey, []);
        buckets.get(p.typeKey).push(p);
      });
      buckets.forEach((b, k) => buckets.set(k, demoteColourways(b.sort(withinCategory))));
      const rankOf = (k) => { const i = prio.findIndex((t) => k === t || k.includes(t) || t.includes(k)); return i === -1 ? 999 : i; };
      const keys = [...buckets.keys()].sort((a, b) => rankOf(a) - rankOf(b) || buckets.get(a)[0].rank - buckets.get(b)[0].rank);
      const listed = keys.filter((k) => rankOf(k) < 999), unlisted = keys.filter((k) => rankOf(k) === 999);
      const weight = (k) => (rankOf(k) < 3 ? 2 : 1); // the sport's first three categories take two slots a round
      const deal = (ks, out) => {
        const pos = new Map(ks.map((k) => [k, 0]));
        let added = true;
        while (added) {
          added = false;
          ks.forEach((k) => { const b = buckets.get(k); for (let n = 0; n < weight(k); n++) { const i = pos.get(k); if (i < b.length) { out.push(b[i]); pos.set(k, i + 1); added = true; } } });
        }
        return out;
      };
      return deal(unlisted, deal(listed, []));
    }

    // Second and later colourways of the same design move after every other design in the category
    function demoteColourways(sorted) {
      const seen = new Set(), first = [], later = [];
      sorted.forEach((p) => { const k = designKey(p); (seen.has(k) ? later : first).push(p); seen.add(k); });
      return first.concat(later);
    }
    function designKey(p) {
      tokens(p);
      return p.vendor + ":" + p.typeKey + ":" + [...p.toks].sort().join(" ");
    }

    // ---- Matching sets (brand + design line, or brand + colourway), one companion per type ----
    function tokens(p) {
      if (p.toks) return p.toks;
      const words = p.title.toLowerCase().replace(/[^a-z0-9/\s-]/g, " ").split(/[\s/-]+/).filter(Boolean);
      const vendorWords = new Set(p.vendor.split(/\s+/));
      p.colours = new Set(words.filter((w) => COLOURS.has(w)));
      if (!p.colours.size) p.colours.add("black"); // no colour word reads as black
      p.toks = new Set(words.filter((w) => w.length > 2 && !TYPE_WORDS.has(w) && !COLOURS.has(w) && !vendorWords.has(w) && !/^\d+(oz)?$/.test(w)));
      return p.toks;
    }
    function matchKind(a, b) { // 2 = same design line or explicit pair tag, 1 = brand + colourway, 0 = none
      if (a.pairs.length && b.pairs.some((t) => a.pairs.includes(t))) return 2;
      if (a.vendor !== b.vendor || a.kids !== b.kids || a.womens !== b.womens) return 0;
      const ta = tokens(a), tb = tokens(b);
      if (![...a.colours].some((c) => b.colours.has(c))) return 0;
      if ([...ta].some((t) => t.length >= 4 && tb.has(t))) return 2;
      if (a.comp && b.comp) return 1; // competition kit: singlet, shorts and headgear by corner colour
      const ga = SET_GROUPS.findIndex((re) => re.test(a.typeKey)), gb = SET_GROUPS.findIndex((re) => re.test(b.typeKey));
      return ga > -1 && ga === gb ? 1 : 0;
    }
    function groupSets(items) {
      const used = new Set(), out = [];
      items.forEach((p) => {
        if (used.has(p)) return;
        used.add(p); out.push(p);
        const types = new Set([p.typeKey]);
        for (let pass = 2; pass >= 1 && types.size < 4; pass--) {
          for (const s of items) {
            if (types.size >= 4) break;
            if (used.has(s) || types.has(s.typeKey) || matchKind(p, s) !== pass) continue;
            // colourway-only sets start from the lead item (a glove pulls its shins and wraps, not the reverse)
            if (pass === 1 && !(p.comp && s.comp) && !/glove|short|singlet|jersey|trunk/.test(p.typeKey)) continue;
            used.add(s); types.add(s.typeKey); out.push(s);
          }
        }
      });
      return out;
    }

    // Boxing + Amateur: the red/blue competition kit leads page 1, one set at a time between regular picks.
    function pinCompKit(items) {
      if (!(state.selected.includes("boxing") && state.level === "amateur")) return items;
      const comp = items.filter(isCompKit);
      if (!comp.length) return items;
      const red = (p) => (/\bred\b/i.test(p.title) ? 0 : 1);
      const sets = groupSets(comp.slice().sort((a, b) => red(a) - red(b) || a.rank - b.rank));
      const firstColour = sets.filter((p) => red(p) === 0);
      const lead = firstColour.length ? firstColour : sets;
      const rest = items.filter((p) => !isCompKit(p));
      const otherCorner = sets.filter((p) => !lead.includes(p)); // the blue sets start page 2
      const out = [];
      let i = 0, j = 0;
      while (i < lead.length || j < rest.length) {
        for (let n = 0; n < 3 && i < lead.length; n++) out.push(lead[i++]);
        for (let n = 0; n < 2 && j < rest.length; n++) out.push(rest[j++]);
      }
      out.splice(Math.min(pageSize(), out.length), 0, ...otherCorner);
      return out;
    }

    function results() {
      let items = pool();
      const cats = new Map();
      items.forEach((p) => { const c = cats.get(p.typeKey) || { label: p.type, n: 0 }; c.n++; cats.set(p.typeKey, c); });
      if (state.category !== "all" && !cats.has(state.category)) state.category = "all";
      if (state.category !== "all") items = items.filter((p) => p.typeKey === state.category);
      if (state.sort === "recommended") items = pinCompKit(groupSets(rotate(items)));
      else {
        const dir = state.sort === "price-desc" ? -1 : 1;
        items = items.slice().sort((a, b) => dir * (a.price - b.price) || a.rank - b.rank);
      }
      return { items, cats };
    }

    // ---------------- Variants ----------------
    const pickable = (p) => p.variants.length > 1;
    function varyingIndexes(p) {
      if (p.variants.length < 2) return [];
      return p.variants[0].parts.map((_, i) => i).filter((i) => new Set(p.variants.map((v) => v.parts[i])).size > 1);
    }
    function variantLabel(p, v) {
      const keep = varyingIndexes(p);
      return keep.length ? keep.map((i) => v.parts[i]).join(" / ") : v.title;
    }
    function optionNoun(p) {
      const names = varyingIndexes(p).map((i) => p.options[i] || "Option");
      return names.length ? names.join(" / ") : "Option";
    }

    // ---------------- Kit maths ----------------
    function kitLead() {
      const counts = {};
      let lead = null;
      state.armed.forEach((a) => (a.ds || []).forEach((d) => {
        counts[d] = (counts[d] || 0) + 1;
        if (!lead || counts[d] > counts[lead]) lead = d;
      }));
      if (!lead && state.selected.length) lead = state.selected[0];
      return { key: lead, count: lead ? counts[lead] || 0 : 0 };
    }
    const kitTotal = () => [...state.armed.values()].reduce((s, a) => s + a.price, 0);
    const kitMet = () => kitLead().count >= cfg.kitThreshold;
    const factor = () => 1 - cfg.discountPct / 100;
    const discountedTotal = () => kitTotal() * factor();
    const sportLabel = (k) => (discByKey[k] ? discByKey[k].label : "");

    // ---------------- Persistence ----------------
    function save() {
      const s = {
        v: 2, step: state.step, selected: state.selected, level: state.level, levelOnly: state.levelOnly,
        sort: state.sort, category: state.category, signup: state.signup, armed: [...state.armed.entries()],
      };
      try { sessionStorage.setItem("dsx-segment", JSON.stringify(s)); } catch (e) { /* blocked */ }
    }
    function restore() {
      try {
        const s = JSON.parse(sessionStorage.getItem("dsx-segment") || "null");
        if (!s || s.v !== 2) return;
        state.selected = (s.selected || []).filter((k) => discByKey[k]);
        state.level = LEVELS.some((l) => l.key === s.level) ? s.level : null;
        state.levelOnly = s.levelOnly !== false;
        state.sort = ["recommended", "price-asc", "price-desc"].includes(s.sort) ? s.sort : "recommended";
        state.category = s.category || "all";
        state.signup = ["signed", "dismissed"].includes(s.signup) ? s.signup : "open";
        (s.armed || []).forEach(([h, a]) => { if (a && a.variantId && Array.isArray(a.ds)) state.armed.set(h, a); });
        if (s.step === "results" && state.selected.length && state.level) state.step = "results";
        else if (s.step === "level" && state.selected.length) state.step = "level";
      } catch (e) { /* ignore */ }
    }
    function reset() {
      state.step = "discipline"; state.selected = []; state.level = null; state.levelOnly = true;
      state.sort = "recommended"; state.category = "all"; state.armed.clear(); state.picking = null;
      save(); renderAll(); focusStage();
    }

    // ---------------- Rendering ----------------
    function renderSteps() {
      root.dataset.step = state.step;
      show("[data-sbd-stage='discipline']", state.step === "discipline");
      show("[data-sbd-stage='level']", state.step === "level");
      show("[data-sbd-stage='results']", state.step === "results");
      setText("[data-sbd-stepcount]", state.step === "discipline" ? cfg.copy.step1 : state.step === "level" ? cfg.copy.step2 : "");
    }

    function renderDisciplines() {
      const wrap = q("[data-sbd-dkeys]");
      if (!wrap) return;
      wrap.style.setProperty("--dcount", disciplines.length);
      wrap.innerHTML = disciplines.map((d) => `
        <button class="fs-dkey" type="button" data-disc="${esc(d.key)}" aria-pressed="${state.selected.includes(d.key)}">
          <span class="fs-dkey-top"><span class="fs-dkey-name">${esc(d.label)}</span><span class="fs-led"></span></span>
          <span class="fs-dkey-desc">${esc(d.desc)}</span>
        </button>`).join("");
      const next = q("[data-sbd-next]");
      if (next) next.disabled = !state.selected.length;
    }

    function renderLevelStep() {
      const wrap = q("[data-sbd-levels]");
      if (wrap) {
        wrap.innerHTML = LEVELS.map((l) => `
          <button class="dsx-lvl" type="button" role="radio" data-level="${l.key}" aria-checked="${state.level === l.key}"
            tabindex="${state.level === l.key || (!state.level && l.key === "beginner") ? 0 : -1}">
            <span class="dsx-lvl-top"><span class="dsx-lvl-name">${l.label}</span><span class="fs-led"></span></span>
            <span class="dsx-lvl-desc">${esc(cfg.copy.levelDescs[l.key])}</span>
          </button>`).join("");
      }
      const dial = q("[data-sbd-dial]");
      const lv = LEVELS.find((l) => l.key === state.level);
      if (dial) dial.style.setProperty("--knob-angle", (lv ? lv.angle : -90) + "deg");
      setText("[data-sbd-level-sub]", fill(cfg.copy.levelSub, { sport: state.selected.map(sportLabel).join(" + ") }));
    }

    function renderResultsHead() {
      setText("[data-sbd-rtitle]", fill(cfg.copy.resultsHeadline, { level: state.level ? LEVELS.find((l) => l.key === state.level).label : "" }));
      setText("[data-sbd-rsub]", state.level ? cfg.copy.levelSubs[state.level] : "");
      setHTML("[data-sbd-sports]", disciplines.map((d) => `
        <button class="dsx-chip" type="button" data-sport="${esc(d.key)}" aria-pressed="${state.selected.includes(d.key)}">${esc(d.label)}</button>`).join(""));
      setHTML("[data-sbd-levelseg]", LEVELS.map((l) => `
        <button class="dsx-seg" type="button" role="radio" data-rlevel="${l.key}" aria-checked="${state.level === l.key}"
          tabindex="${state.level === l.key ? 0 : -1}">${l.label}</button>`).join(""));
      const sw = q("[data-sbd-levelonly]");
      if (sw) { sw.setAttribute("aria-checked", state.levelOnly); sw.hidden = !state.selected.some((k) => (discByKey[k].levelMode || "full") !== "none"); }
      const sort = q("[data-sbd-sort]");
      if (sort) sort.value = state.sort;
    }

    function pageSize() { return MOBILE.matches ? cfg.pageSizeMobile : cfg.pageSizeDesktop; }

    function renderResults(resetPage, scroll) {
      renderResultsHead();
      const grid = q("[data-sbd-grid]");
      if (!grid) return;
      const loading = state.selected.some((k) => status[k] !== "ready" && status[k] !== "error");
      const failed = state.selected.length && state.selected.every((k) => status[k] === "error");
      state.picking = null;
      if (failed) {
        grid.innerHTML = "";
        show("[data-sbd-more]", false);
        showEmpty(cfg.copy.loadError, "retry");
        return;
      }
      const { items, cats } = results();
      if (resetPage || !state.shown) state.shown = pageSize();
      renderCats(cats);

      if (loading && !items.length) {
        grid.setAttribute("aria-busy", "true");
        grid.innerHTML = Array.from({ length: pageSize() }, () => `<li class="dsx-card is-loading" aria-hidden="true"><span class="dsx-media"></span><span class="dsx-cbody"><span class="dsx-cname"></span><span class="dsx-cprice">&nbsp;</span></span></li>`).join("");
        show("[data-sbd-more]", false);
        showEmpty(false);
        setText("[data-sbd-showing]", cfg.copy.loading);
        return;
      }
      grid.setAttribute("aria-busy", String(loading));
      if (!items.length) {
        grid.innerHTML = "";
        show("[data-sbd-more]", false);
        setText("[data-sbd-showing]", "");
        showEmpty(levelFilterOn() ? cfg.copy.emptyFilters : cfg.copy.emptyNone, levelFilterOn() ? "levelonly-off" : "");
        return;
      }
      showEmpty(false);
      const shown = items.slice(0, state.shown);
      grid.innerHTML = shown.map((p, i) => cardHTML(p, i)).join("");
      setText("[data-sbd-showing]", fill(cfg.copy.showing, { shown: shown.length, total: items.length }));
      show("[data-sbd-more]", shown.length < items.length);
      setText("[data-sbd-morecount]", fill(cfg.copy.moreCount, { left: items.length - shown.length }));
      if (scroll) scrollToResults();
    }

    // Category chips: All + the first N priority types present in the results (topped up by size), the rest under More
    function renderCats(cats) {
      const total = [...cats.values()].reduce((s, c) => s + c.n, 0);
      const limit = Math.max(0, cfg.catChips);
      const shown = [];
      priorityList().forEach((k) => { if (shown.length < limit && cats.has(k) && !shown.includes(k)) shown.push(k); });
      [...cats.entries()].sort((a, b) => b[1].n - a[1].n).forEach(([k]) => { if (shown.length < limit && !shown.includes(k)) shown.push(k); });
      const rest = [...cats.entries()].filter(([k]) => !shown.includes(k)).sort((a, b) => b[1].n - a[1].n);
      const chip = (k, label, n) => `<button class="dsx-chip" type="button" data-cat="${esc(k)}" aria-pressed="${state.category === k}">${esc(label)} <span class="dsx-count">${n}</span></button>`;
      const inMore = rest.some(([k]) => k === state.category);
      const moreLabel = inMore ? `${cfg.copy.more}: ${cats.get(state.category).label}` : cfg.copy.more;
      setHTML("[data-sbd-cats]", [chip("all", "All", total)]
        .concat(shown.map((k) => chip(k, cats.get(k).label, cats.get(k).n)))
        .concat(rest.length ? [`<button class="dsx-chip dsx-chip-more" type="button" data-sbd-morecats aria-expanded="${state.moreOpen}" aria-controls="${cfg.id}-morecats" aria-pressed="${inMore}">${esc(moreLabel)} <span class="dsx-count">${inMore ? cats.get(state.category).n : rest.reduce((s, [, c]) => s + c.n, 0)}</span></button>`] : [])
        .join(""));
      const panel = q("[data-sbd-morepanel]");
      if (panel) {
        panel.hidden = !(state.moreOpen && rest.length);
        panel.innerHTML = rest.map(([k, c]) => chip(k, c.label, c.n)).join("");
      }
    }

    function cardHTML(p, i) {
      const a = state.armed.get(p.handle);
      const price = a ? a.price : p.price;
      const badge = state.level && matchesLevel(p) ? `<span class="dsx-badge">${esc(cfg.copy.badge)}</span>` : "";
      const label = a ? `${cfg.copy.inKit}${a.label ? " · " + a.label : ""}` : cfg.copy.addToKit;
      const picking = state.picking === p.handle;
      const pickId = `${cfg.id}-pick-${i}`;
      const expand = pickable(p) && !a ? `aria-expanded="${picking}" aria-controls="${pickId}"` : "";
      return `<li class="dsx-card${a ? " is-armed" : ""}" data-handle="${esc(p.handle)}" data-index="${i}">
        <a class="dsx-media" href="${esc(p.url)}" tabindex="-1" aria-hidden="true">
          <img src="${esc(p.image)}" alt="" width="400" height="400" loading="${i < 4 ? "eager" : "lazy"}" decoding="async">
        </a>
        <div class="dsx-cbody">
          ${badge}
          <a class="dsx-cname" href="${esc(p.url)}">${esc(p.title)}</a>
          <span class="dsx-cprice">${money(price)}</span>
          <button class="dsx-add" type="button" data-arm="${esc(p.handle)}" aria-pressed="${!!a}" ${expand}
            aria-label="${esc(`${p.title}, ${money(price)}. ${a ? "In your kit, press to remove" : pickable(p) ? "Pick " + optionNoun(p).toLowerCase() + " to add to kit" : "Add to kit"}`)}">${esc(label)}</button>
        </div>
        ${picking ? pickerHTML(p, pickId) : ""}
      </li>`;
    }

    function pickerHTML(p, id) {
      const noun = optionNoun(p);
      const keys = p.variants.map((v) => {
        const name = variantLabel(p, v);
        const extra = (v.available ? "" : ", sold out") + (v.price !== p.price ? ", " + money(v.price) : "");
        return `<button class="dsx-variant" type="button" data-variant="${v.id}" data-handle="${esc(p.handle)}" ${v.available ? "" : "disabled"} aria-label="${esc(name + extra)}">${esc(name)}</button>`;
      }).join("");
      return `<div class="dsx-picker" id="${id}" role="group" aria-label="${esc(`Pick ${noun.toLowerCase()} for ${p.title}`)}">
        <div class="dsx-picker-head"><span class="fs-label-s">Pick ${esc(noun)}</span>
          <button class="dsx-picker-close" type="button" data-pick-close="${esc(p.handle)}" aria-label="Close without adding">×</button></div>
        <div class="dsx-picker-grid">${keys}</div>
      </div>`;
    }

    function refreshCard(handle, focusSel) {
      const find = () => q(`[data-sbd-grid] li[data-handle="${CSS.escape(handle)}"]`);
      const li = find();
      const p = byHandle.get(handle);
      if (!li || !p) return null;
      li.outerHTML = cardHTML(p, +li.dataset.index);
      const fresh = find();
      const target = fresh && focusSel && fresh.querySelector(focusSel);
      if (target) target.focus();
      return fresh;
    }

    function showEmpty(message, action) {
      const box = q("[data-sbd-empty]");
      if (!box) return;
      box.hidden = !message;
      if (!message) return;
      const btn = action === "retry" ? `<button class="fs-btn" type="button" data-action="retry">${esc(cfg.copy.retry)}</button>`
        : action === "levelonly-off" ? `<button class="fs-btn" type="button" data-action="levelonly-off">${esc(cfg.copy.showAllTiers)}</button>` : "";
      box.innerHTML = `<p>${esc(message)}</p>${btn}`;
    }

    function renderKit(tick) {
      const { key, count } = kitLead();
      const n = cfg.kitThreshold;
      const met = count >= n;
      const signed = state.signup === "signed";
      const readout = q("[data-sbd-readout]");
      setText("[data-sbd-digits]", `${Math.min(count, n)} / ${n}`);
      if (readout) {
        readout.classList.toggle("is-met", met);
        if (tick && !reduceMotion.matches) { readout.classList.remove("is-tick"); void readout.offsetWidth; readout.classList.add("is-tick"); }
      }
      const vars = { n: Math.max(n - count, 0), sport: sportLabel(key), pct: cfg.discountPct, items: n - count === 1 ? "item" : "items", count, threshold: n };
      let msg;
      if (!state.armed.size) msg = fill(cfg.copy.kitEmpty, vars);
      else if (!met) msg = fill(cfg.copy.kitProgress, vars);
      else if (signed) msg = fill(cfg.copy.kitUnlocked, vars);
      else if (state.signup === "dismissed") msg = fill(cfg.copy.kitDeclined || "{count} items picked. Your kit is at the regular price.", vars);
      else msg = fill(cfg.copy.kitUnlockedSignup, vars);
      // MMAFS QA-02 (29 Oct 2026): after "No thanks", stop pointing at a hidden form and offer a way back to the deal
      const reopen = state.signup === "dismissed" && state.armed.size && cfg.copy.reopen
        ? ` <button class="fs-link dsx-reopen" type="button" data-action="reopen">${esc(fill(cfg.copy.reopen, vars))}</button>` : "";
      setHTML("[data-sbd-kitmsg]", esc(msg) + reopen);
      const total = kitTotal();
      const countLine = `${state.armed.size} ${state.armed.size === 1 ? "item" : "items"}`;
      setHTML("[data-sbd-kittotal]", state.kitError ? esc(state.kitError)
        : met && signed ? `<s>${money(total)}</s>${money(discountedTotal())}<small>${esc(countLine)}</small>`
        : `${money(total)}<small>${esc(countLine)}</small>`);
      const add = q("[data-sbd-addkit]");
      if (add) add.disabled = !state.armed.size;
      show("[data-sbd-dock]", state.step === "results");
    }

    function renderOffer() {
      const box = q("[data-sbd-offer]");
      if (!box) return;
      const open = state.signup === "open" && state.armed.size > 0 && state.step === "results";
      const signed = state.signup === "signed" && state.step === "results";
      box.hidden = !(open || signed);
      show("[data-sbd-form]", open);
      const { key, count } = kitLead();
      const n = cfg.kitThreshold;
      const vars = { n: Math.max(n - count, 0), sport: sportLabel(key), pct: cfg.discountPct, items: n - count === 1 ? "item" : "items", threshold: n };
      if (open) setText("[data-sbd-offermsg]", count >= n ? fill(cfg.copy.offerMet, vars) : fill(cfg.copy.offer, vars));
      if (signed) setHTML("[data-sbd-offermsg]", fill(esc(cfg.copy.signedMsg), { ...vars, code: `<span class="dsx-code">${esc(cfg.discountCode)}</span>` }));
      const err = q("[data-sbd-error]");
      if (err) { err.hidden = !state.error; err.textContent = state.error; }
      const email = q("[data-sbd-email]");
      if (email) email.toggleAttribute("aria-invalid", !!state.error);
    }

    function renderAll() {
      renderSteps();
      renderDisciplines();
      renderLevelStep();
      if (state.step === "results") renderResults(true);
      renderKit(false);
      renderOffer();
    }

    function focusStage() {
      const el = q(`[data-sbd-stage='${state.step}'] h2`);
      if (el) { el.setAttribute("tabindex", "-1"); el.focus({ preventScroll: true }); }
    }
    function scrollToResults() {
      const head = q("[data-sbd-rhead]");
      if (!head) return;
      const top = head.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.4) head.scrollIntoView({ behavior: reduceMotion.matches ? "auto" : "smooth", block: "start" });
    }
    function flash(led) {
      if (!led || reduceMotion.matches) return;
      led.classList.remove("is-flash"); void led.offsetWidth; led.classList.add("is-flash");
    }

    // ---------------- Actions ----------------
    function goLevel() {
      if (!state.selected.length) return;
      state.selected.forEach(load); // prefetch while the shopper picks a level
      state.step = "level";
      save(); renderAll(); focusStage();
    }
    function goResults(level) {
      state.level = level;
      state.step = "results";
      state.category = "all";
      state.selected.forEach(load);
      save();
      renderSteps(); renderLevelStep();
      renderResults(true, true);
      renderKit(false); renderOffer();
      const h = q("[data-sbd-rtitle]");
      if (h) { h.setAttribute("tabindex", "-1"); h.focus({ preventScroll: true }); }
    }

    function toggleDiscipline(key) {
      if (MOBILE.matches) { // tap to advance
        state.selected = [key];
        return goLevel();
      }
      const i = state.selected.indexOf(key);
      if (i === -1) { state.selected.push(key); load(key); } else state.selected.splice(i, 1);
      save(); renderDisciplines();
      const btn = q(`[data-disc="${CSS.escape(key)}"]`);
      if (btn) { btn.focus(); flash(btn.querySelector(".fs-led")); }
    }

    function toggleSportChip(key) {
      const i = state.selected.indexOf(key);
      if (i > -1 && state.selected.length === 1) return; // keep at least one sport
      if (i === -1) { state.selected.push(key); load(key); } else state.selected.splice(i, 1);
      save(); renderResults(true); renderKit(false); renderOffer();
      const chip = q(`[data-sport="${CSS.escape(key)}"]`);
      if (chip) chip.focus();
    }

    function setResultLevel(level, focus) {
      if (level === state.level) return;
      state.level = level;
      save(); renderLevelStep(); renderResults(true); renderKit(false); renderOffer();
      if (focus) { const b = q(`[data-rlevel="${level}"]`); if (b) b.focus(); }
    }

    function toggleArm(handle) {
      const p = byHandle.get(handle);
      if (!p) return;
      state.kitError = "";
      if (state.armed.has(handle)) { state.armed.delete(handle); return kitChanged(handle); }
      if (pickable(p)) return openPicker(handle);
      arm(p, p.firstAvail || p.variants[0]);
    }
    function arm(p, v) {
      const ds = p.ds.filter((d) => state.selected.includes(d));
      state.armed.set(p.handle, { ds: ds.length ? ds : p.ds.slice(0, 1), variantId: v.id, label: pickable(p) ? variantLabel(p, v) : "", price: v.price, title: p.title });
      state.picking = null;
      kitChanged(p.handle);
    }
    function openPicker(handle) {
      const prev = state.picking;
      state.picking = handle;
      if (prev && prev !== handle) refreshCard(prev);
      refreshCard(handle, ".dsx-variant:not([disabled])");
    }
    function closePicker(handle) { state.picking = null; refreshCard(handle, ".dsx-add"); }
    function chooseVariant(handle, id) {
      const p = byHandle.get(handle);
      const v = p && p.variants.find((x) => String(x.id) === String(id));
      if (v && v.available) arm(p, v);
    }
    function kitChanged(handle) {
      const li = refreshCard(handle, ".dsx-add");
      if (li) flash(li.querySelector(".fs-led"));
      save(); renderKit(true); renderOffer();
    }

    async function addKitToCart() {
      const armed = [...state.armed.values()];
      if (!armed.length) return;
      const discount = kitMet() && state.signup === "signed";
      const attributes = { discipline: state.selected.join(","), experience_level: state.level || "" };
      save();
      if (cfg.cartMode === "permalink") {
        const qs = new URLSearchParams();
        Object.entries(attributes).forEach(([k, v]) => qs.set(`attributes[${k}]`, v));
        if (discount) qs.set("discount", cfg.discountCode);
        window.open(`${cfg.permalinkStore}/cart/${armed.map((a) => a.variantId + ":1").join(",")}?${qs}`, "_blank", "noopener");
        return;
      }
      const btn = q("[data-sbd-addkit]");
      if (btn) { btn.disabled = true; btn.setAttribute("aria-busy", "true"); }
      try {
        const post = (path, body) => fetch(cfg.root + path, {
          method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body),
        }).then(async (r) => { if (!r.ok) throw await r.json().catch(() => ({})); return r.json(); });
        await post("cart/add.js", { items: armed.map((a) => ({ id: a.variantId, quantity: 1 })) });
        await post("cart/update.js", { attributes });
        window.location.href = discount
          ? `${cfg.root}discount/${encodeURIComponent(cfg.discountCode)}?redirect=${encodeURIComponent(cfg.root + "cart")}`
          : `${cfg.root}cart`;
      } catch (err) {
        state.kitError = (err && (err.description || err.message)) || cfg.copy.cartError;
        renderKit(false);
        if (btn) { btn.disabled = false; btn.removeAttribute("aria-busy"); }
      }
    }

    function submitSignup(e) {
      const form = e.target;
      const emailEl = q("[data-sbd-email]");
      const email = emailEl ? emailEl.value.trim() : "";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        e.preventDefault(); state.error = cfg.copy.emailError; renderOffer(); if (emailEl) emailEl.focus(); return;
      }
      const consent = q("[data-sbd-consent]");
      if (consent && !consent.checked) {
        e.preventDefault(); state.error = cfg.copy.consentError; renderOffer(); consent.focus(); return;
      }
      state.error = "";
      const tags = q("[data-sbd-tags]");
      if (tags) tags.value = state.selected.map((d) => `discipline:${d}`).concat(`level:${state.level}`, "source:discipline-quiz").join(",");
      if (cfg.signupMode === "simulate") {
        e.preventDefault();
        console.info("[sbd] customer signup (simulated)", { email, tags: tags && tags.value });
      } else {
        // Shopify handles the POST in a hidden iframe so the shopper stays on the results.
        let sink = document.querySelector("iframe[name='mfs-sbd-sink']");
        if (!sink) {
          sink = document.createElement("iframe");
          sink.name = "mfs-sbd-sink"; sink.hidden = true; sink.title = "Signup";
          document.body.appendChild(sink);
        }
        form.target = "mfs-sbd-sink";
      }
      state.signup = "signed";
      save();
      setTimeout(() => { renderKit(false); renderOffer(); const o = q("[data-sbd-offer]"); if (o) o.focus(); }, 0);
    }

    // ---------------- Events ----------------
    root.addEventListener("click", (e) => {
      refreshIfStale();
      const t = e.target.closest("[data-disc],[data-level],[data-sport],[data-rlevel],[data-cat],[data-sbd-morecats],[data-arm],[data-variant],[data-pick-close],[data-action],[data-sbd-next],[data-sbd-cross],[data-sbd-levelonly],[data-sbd-addkit],[data-sbd-more],[data-sbd-dial]");
      if (!t || !root.contains(t)) return;
      const ds = t.dataset;
      if (ds.variant) return chooseVariant(ds.handle, ds.variant);
      if (ds.pickClose) return closePicker(ds.pickClose);
      if (ds.disc) return toggleDiscipline(ds.disc);
      if (ds.level) return goResults(ds.level);
      if (ds.sport) return toggleSportChip(ds.sport);
      if (ds.rlevel) return setResultLevel(ds.rlevel);
      if (t.hasAttribute("data-sbd-morecats")) { state.moreOpen = !state.moreOpen; renderResults(false); const m = q("[data-sbd-morecats]"); if (m) m.focus(); return; }
      if (ds.cat) { state.category = ds.cat; state.moreOpen = false; save(); renderResults(true); const c = q(`[data-cat="${CSS.escape(ds.cat)}"]`); if (c) c.focus(); return; }
      if (ds.arm) return toggleArm(ds.arm);
      if (t.hasAttribute("data-sbd-next")) return goLevel();
      if (t.hasAttribute("data-sbd-cross")) { state.selected = disciplines.map((d) => d.key); return goLevel(); }
      if (t.hasAttribute("data-sbd-dial")) {
        const i = LEVELS.findIndex((l) => l.key === state.level);
        state.level = LEVELS[(i + 1) % LEVELS.length].key;
        return renderLevelStep();
      }
      if (t.hasAttribute("data-sbd-levelonly") || ds.action === "levelonly-off") {
        state.levelOnly = ds.action ? false : !state.levelOnly;
        save(); return renderResults(true);
      }
      if (t.hasAttribute("data-sbd-more")) {
        const before = state.shown;
        state.shown += pageSize(); renderResults(false);
        const next = q(`[data-sbd-grid] li[data-index="${before}"] .dsx-cname`);
        if (next) next.focus();
        return;
      }
      if (t.hasAttribute("data-sbd-addkit")) return addKitToCart();
      switch (ds.action) {
        case "back-sport": state.step = "discipline"; save(); renderAll(); return focusStage();
        case "change": state.step = "discipline"; save(); renderAll(); focusStage(); return root.scrollIntoView({ block: "start" });
        case "reset": return reset();
        case "retry": state.selected.forEach((k) => { if (status[k] === "error") { delete loads[k]; load(k); } }); return renderResults(false);
        case "dismiss": state.signup = "dismissed"; state.error = ""; save(); renderKit(false); return renderOffer();
        case "reopen": { state.signup = "open"; state.error = ""; save(); renderKit(false); renderOffer(); const o = q("[data-sbd-offer]"); if (o) o.focus(); return; }
        default:
      }
    });

    const form = q("[data-sbd-form]");
    if (form) form.addEventListener("submit", submitSignup);
    const sort = q("[data-sbd-sort]");
    if (sort) sort.addEventListener("change", (e) => { state.sort = e.target.value; save(); renderResults(true); });

    root.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && state.picking) { e.preventDefault(); return closePicker(state.picking); }
      if (e.key === "Escape" && state.moreOpen) { e.preventDefault(); state.moreOpen = false; renderResults(false); const m = q("[data-sbd-morecats]"); if (m) m.focus(); return; }
      const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
      if (!dir) return;
      const t = e.target;
      if (t.dataset.level) { // level step: arrows move focus; Enter/Space chooses
        e.preventDefault();
        const i = LEVELS.findIndex((l) => l.key === t.dataset.level);
        const next = LEVELS[(i + dir + LEVELS.length) % LEVELS.length].key;
        state.level = next; renderLevelStep();
        const b = q(`[data-level="${next}"]`); if (b) b.focus();
      } else if (t.dataset.rlevel) {
        e.preventDefault();
        const i = LEVELS.findIndex((l) => l.key === t.dataset.rlevel);
        setResultLevel(LEVELS[(i + dir + LEVELS.length) % LEVELS.length].key, true);
      }
    });

    // Page size follows the viewport
    MOBILE.addEventListener("change", () => { if (state.step === "results") renderResults(true); });
    // Back to the tab, or back from a product page restored from the browser's page cache
    document.addEventListener("visibilitychange", refreshIfStale);
    window.addEventListener("pageshow", (e) => { if (e.persisted) refreshIfStale(); });

    restore();
    renderAll();
    if (state.step !== "discipline") state.selected.forEach(load);
    root.sbd = { state, cfg, byHandle, status, results }; // for the console and tests
  }

  function boot(scope) {
    (scope || document).querySelectorAll("[data-sbd]:not([data-sbd-ready])").forEach((root) => {
      root.setAttribute("data-sbd-ready", "");
      try { init(root); } catch (err) { console.error("[sbd]", err); }
    });
  }
  window.MfsSbd = { boot };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => boot());
  else boot();
  document.addEventListener("shopify:section:load", (e) => boot(e.target));
})();
